<template>
  <ul :class="containerClasses">
    <li
      v-for="(option, index) in options"
      :key="`${option.value}`"
      class="v-radio-control-ui__item"
    >
      <VRadio
        v-model="model"
        :value="option.value"
        :label="option.label"
        :name="name"
        :class="getRadioClasses(index)"
      />
    </li>
  </ul>
</template>

<script setup lang="ts">
  // Types
  import type { TRadioControl } from '~/misc/v-radio-control/types';
  import { EDisplayMode } from '~/misc/v-radio-control/types';

  // Components
  import VRadio from '~/misc/v-radio';

  const props = defineProps({
    options: {
      type: Array as PropType<TRadioControl[]>,
      required: true,
    },
    name: {
      type: String,
      required: true,
    },
    radioClass: {
      type: String,
      default: '',
    },
    displayMode: {
      type: String as PropType<EDisplayMode>,
      default: EDisplayMode.LIST,
      validator: (value: EDisplayMode) => Object.values(EDisplayMode).includes(value),
    },
  });

  const model = defineModel({
    type: [String, Number, Boolean] as PropType<string | number | boolean>,
    default: undefined,
  });

  const containerClasses: ComputedRef<string[]> = computed<string[]>(() => {
    const classes: string[] = ['v-radio-control-ui'];

    if (props.displayMode === EDisplayMode.BUTTON_GROUP) {
      classes.push('button-group');
    }

    return classes;
  });

  const getRadioClasses = (index: number): string => {
    if (props.displayMode === EDisplayMode.BUTTON_GROUP) {
      const classes: string[] = ['button', 'slim'];

      if (props.radioClass) {
        classes.push(props.radioClass);
      }

      if (props.options.length === 1) {
        // Single option - no start/end classes needed
        return classes.join(' ');
      }

      if (index === 0) {
        classes.push('start');
      } else if (index === props.options.length - 1) {
        classes.push('end');
      } else {
        classes.push('middle');
      }

      return classes.join(' ');
    }

    // For list mode, return the radioClass prop as is
    return props.radioClass;
  };
</script>
